import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import {
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { sessionService } from "../services/sessionService";
import { trainingTypeService } from "../services/trainingTypeService";
import { asDisplayNameOrNull } from "../utils/displayName";
import { Session } from "../types/post";
import { TrainingType } from "../types/activity";
import { DeleteConfirmDialog } from "../components/DayPost";

const PER_PAGE = 10;
type SortField = "date" | "name";
type SortDir = "asc" | "desc";
type LinkedFilter = "all" | "linked" | "unlinked";
type SessionRow = Session & { id: string };

const formatPlannedDate = (val?: string): string => {
  if (!val) return "—";
  const d = new Date(val.length === 10 ? `${val}T00:00:00` : val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getDisplayName = (
  session: SessionRow,
  trainingTypes: TrainingType[],
): string => {
  const name = asDisplayNameOrNull(session.name);
  if (name) return name;
  const typeName = trainingTypeService.getSessionTrainingTypeName(
    session,
    trainingTypes,
  );
  if (typeName) return typeName;
  // Last-resort fallback for legacy composite IDs: `${sportId}:${name}`
  if (session.sessionTypeID?.includes(":")) {
    const part = session.sessionTypeID.split(":")[1];
    if (part) return part;
  }
  return "Session";
};

const CompletedSessions: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [linkedFilter, setLinkedFilter] = useState<LinkedFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<SessionRow | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentUser?.uid) return;
    setLoading(true);
    try {
      const [all, types] = await Promise.all([
        sessionService.getAllSessions(currentUser.uid),
        trainingTypeService.fetchTrainingTypes(),
      ]);
      setSessions(all.filter((s) => s.status === "complete"));
      setTrainingTypes(types);
    } catch (err) {
      console.error("Failed to load completed sessions:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedSessions = useMemo(() => {
    const filtered = sessions.filter((s) => {
      if (linkedFilter === "linked") return !!s.linkedTrainingLogID;
      if (linkedFilter === "unlinked") return !s.linkedTrainingLogID;
      return true;
    });
    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortField === "date") {
        cmp = (a.plannedDate || "").localeCompare(b.plannedDate || "");
      } else {
        cmp = getDisplayName(a, trainingTypes).localeCompare(
          getDisplayName(b, trainingTypes),
          undefined,
          { sensitivity: "base" },
        );
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return filtered;
  }, [sessions, trainingTypes, sortField, sortDir, linkedFilter]);

  const totalPages = Math.max(1, Math.ceil(sortedSessions.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = sortedSessions.slice(
    (safePage - 1) * PER_PAGE,
    safePage * PER_PAGE,
  );

  const pageIds = pageItems.map((s) => s.id);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "date" ? "desc" : "asc");
    }
    setPage(1);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleConfirmDelete = async () => {
    if (!currentUser?.uid || !deleting) return;
    setActionInFlight(true);
    try {
      await sessionService.deleteSession(currentUser.uid, deleting.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleting.id);
        return next;
      });
      setDeleting(null);
      await fetchData();
    } catch (err) {
      console.error("Failed to delete session:", err);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (!currentUser?.uid || selectedIds.size === 0) return;
    setActionInFlight(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map(async (id) => {
          try {
            await sessionService.deleteSession(currentUser.uid, id);
          } catch (err) {
            console.error("Failed to delete session", id, err);
          }
        }),
      );
      clearSelection();
      setBulkDeleting(false);
      await fetchData();
    } finally {
      setActionInFlight(false);
    }
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return (
        <ChevronUpDownIcon className="h-4 w-4 inline-block text-base-content/40" />
      );
    }
    return (
      <span className="text-base-content/70 text-xs ml-1">
        {sortDir === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="pt-16 pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6 pt-8 px-8 sm:px-0">
            <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-base-content font-heading">
              Sessions Completed
            </h1>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setBulkDeleting(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-500/15 text-red-600 text-sm font-medium"
              >
                <TrashIcon className="h-4 w-4" />
                Delete {selectedIds.size} selected
              </button>
            )}
          </div>

          <div className="px-4 sm:px-0">
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
              <span className="mt-0.5 inline-flex items-center rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
                Beta
              </span>
              <p className="text-sm text-base-content/80">
                This page is in beta and updates are currently being worked on.
                Some features may change or be temporarily unavailable.
              </p>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-base-content/60">Show:</span>
              {(
                [
                  { value: "all", label: "All" },
                  { value: "linked", label: "Linked" },
                  { value: "unlinked", label: "Not linked" },
                ] as { value: LinkedFilter; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setLinkedFilter(opt.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    linkedFilter === opt.value
                      ? "bg-base-content text-base-100 border-base-content"
                      : "bg-base-100 text-base-content/70 border-base-300 hover:bg-base-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="text-center py-16 text-base-content/60">
                Loading sessions...
              </div>
            ) : sortedSessions.length === 0 ? (
              <div className="text-center py-16 text-base-content/60">
                No completed sessions yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={allOnPageSelected}
                          onChange={togglePageSelection}
                          aria-label="Select all on page"
                        />
                      </th>
                      <th>
                        <button
                          type="button"
                          onClick={() => toggleSort("name")}
                          className="flex items-center gap-1 font-heading"
                        >
                          Name {sortIndicator("name")}
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          onClick={() => toggleSort("date")}
                          className="flex items-center gap-1 font-heading"
                        >
                          Date {sortIndicator("date")}
                        </button>
                      </th>
                      <th className="hidden sm:table-cell">Activities</th>
                      <th className="hidden md:table-cell">Training log</th>
                      <th className="w-24 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((session) => {
                      const activityCount =
                        session.ActivityMeasurementList?.length || 0;
                      const hasNotes = (session.activityBlocks || []).some(
                        (b) =>
                          b.blockType === "notes" &&
                          (b.notesContent || "").trim(),
                      );
                      const linked = !!session.linkedTrainingLogID;
                      return (
                        <tr key={session.id} className="hover">
                          <td>
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={selectedIds.has(session.id)}
                              onChange={() => toggleRow(session.id)}
                              aria-label="Select row"
                            />
                          </td>
                          <td className="font-medium">
                            {getDisplayName(session, trainingTypes)}
                          </td>
                          <td>{formatPlannedDate(session.plannedDate)}</td>
                          <td className="hidden sm:table-cell">
                            {activityCount === 0 && hasNotes
                              ? "Notes"
                              : activityCount}
                          </td>
                          <td className="hidden md:table-cell">
                            {linked ? (
                              <span className="text-[10px] font-medium bg-blue-500/15 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
                                Linked
                              </span>
                            ) : (
                              <span className="text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
                                Not logged
                              </span>
                            )}
                          </td>
                          <td className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/session/${session.id}`)
                                }
                                className="p-1.5 rounded hover:bg-base-200"
                                aria-label="View session"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleting(session)}
                                className="p-1.5 rounded hover:bg-base-200 text-red-600"
                                aria-label="Delete session"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {sortedSessions.length > 0 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-base-content/60">
                  Page {safePage} of {totalPages} · {sortedSessions.length}{" "}
                  total
                </span>
                <div className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-2 rounded hover:bg-base-200 disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                    className="p-2 rounded hover:bg-base-200 disabled:opacity-40"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleConfirmDelete}
        logType="session"
        isDeleting={actionInFlight}
      />

      <DeleteConfirmDialog
        open={bulkDeleting}
        onClose={() => setBulkDeleting(false)}
        onConfirm={handleConfirmBulkDelete}
        logType={`${selectedIds.size} session`}
        isDeleting={actionInFlight}
      />

      <BottomNavBar />
    </div>
  );
};

export default CompletedSessions;
